import { useRef } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

const insuranceLines = [
  {
    icon: 'ri-car-line',
    title: 'Seguro Auto',
    description:
      'Mesmo com a apólice vigente, é possível identificar excessos de custo, coberturas desalinhadas ao uso real do veículo e falhas contratuais que comprometem a indenização.',
    items: [
      'Redução de custo durante a vigência',
      'Ajuste de coberturas incompatíveis',
      'Revisão de cláusulas restritivas',
      'Mais proteção para o patrimônio',
    ],
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Plano de Saúde',
    description:
      'A reavaliação técnica ajuda a identificar limitações contratuais, reajustes desproporcionais e oportunidades de melhorar a proteção assistencial com melhor custo-benefício.',
    items: [
      'Revisão de rede e cobertura',
      'Análise de reajustes e custos',
      'Correção de limitações críticas',
      'Mais eficiência para empresa e família',
    ],
  },
  {
    icon: 'ri-shield-cross-line',
    title: 'Seguro de Vida',
    description:
      'Muitas apólices permanecem anos sem atualização. A reavaliação permite corrigir capitais inadequados, exclusões sensíveis e estruturas desalinhadas à realidade atual do segurado.',
    items: [
      'Atualização de capitais segurados',
      'Revisão de exclusões contratuais',
      'Adequação ao perfil atual',
      'Mais segurança para família e empresa',
    ],
  },
  {
    icon: 'ri-fire-line',
    title: 'Incêndio Empresarial',
    description:
      'A revisão do seguro de incêndio pode eliminar lacunas de cobertura, corrigir subavaliação patrimonial e reduzir custos em apólices mal negociadas.',
    items: [
      'Revisão de LMI e coberturas',
      'Análise patrimonial detalhada',
      'Negociação técnica com seguradoras',
      'Proteção real para ativos e operações',
    ],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Seguros Empresariais',
    description:
      'Empresas frequentemente mantêm contratos com falhas invisíveis. A reavaliação permite blindar cláusulas, revisar exposições e melhorar a relação entre custo e proteção.',
    items: [
      'Mapeamento de vulnerabilidades',
      'Blindagem contratual',
      'Melhor custo-benefício',
      'Proteção alinhada ao risco real',
    ],
  },
  {
    icon: 'ri-home-4-line',
    title: 'Seguros Patrimoniais',
    description:
      'Patrimônio protegido exige coerência entre valor exposto, cobertura contratada e risco real. A reavaliação corrige desequilíbrios antes que o prejuízo aconteça.',
    items: [
      'Revisão de valores em risco',
      'Correção de subseguro',
      'Ajuste de coberturas essenciais',
      'Mais segurança patrimonial',
    ],
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Leitura Técnica da Apólice Vigente',
    duration: '1–2 dias úteis',
    description:
      'Analisamos a apólice atual para identificar cláusulas restritivas, coberturas insuficientes, exclusões relevantes, capitais inadequados e pontos de fragilidade contratual.',
    deliverables: [
      'Diagnóstico inicial da apólice',
      'Mapa de falhas contratuais',
      'Identificação de lacunas de cobertura',
      'Primeiras oportunidades de economia',
    ],
  },
  {
    number: '02',
    title: 'Avaliação de Riscos e Necessidades Atuais',
    duration: '2–3 dias úteis',
    description:
      'Revisamos o perfil real do segurado, patrimônio exposto, operações, uso efetivo e mudanças recentes para verificar se o contrato atual ainda faz sentido.',
    deliverables: [
      'Levantamento de perfil e exposição',
      'Análise do risco atual',
      'Comparação entre contrato e necessidade real',
      'Definição do nível ideal de proteção',
    ],
  },
  {
    number: '03',
    title: 'Reestruturação Técnica e Negociação',
    duration: '3–5 dias úteis',
    description:
      'Com base no diagnóstico, reposicionamos coberturas, corrigimos falhas, negociamos com corretores, bancos e seguradoras e buscamos melhor custo-benefício.',
    deliverables: [
      'Estrutura técnica recomendada',
      'Comparativo de alternativas',
      'Negociação estratégica',
      'Proposta revisada e justificada',
    ],
  },
  {
    number: '04',
    title: 'Implementação e Monitoramento',
    duration: 'Contínuo',
    description:
      'Após a reavaliação, acompanhamos a adequação contratual, revisões futuras e pontos críticos para manter a proteção eficiente ao longo do tempo.',
    deliverables: [
      'Conferência final da contratação',
      'Validação das coberturas',
      'Acompanhamento técnico contínuo',
      'Suporte em revisões futuras',
    ],
  },
];

const useCases = [
  {
    icon: 'ri-user-line',
    title: 'Pessoa Física',
    color: 'orange',
    description:
      'Segurados que já possuem apólices ativas, mas querem pagar menos e corrigir falhas ocultas em contratos de auto, vida, residencial ou saúde.',
    risks: [
      'Pagar caro por cobertura inadequada',
      'Descobrir exclusões só no sinistro',
      'Manter apólices desatualizadas',
      'Falsa sensação de segurança',
    ],
  },
  {
    icon: 'ri-briefcase-4-line',
    title: 'Empresas',
    color: 'petrol',
    description:
      'Negócios que possuem seguros empresariais, incêndio, responsabilidade civil, saúde ou vida em grupo e precisam revisar estrutura, custo e blindagem contratual.',
    risks: [
      'Coberturas abaixo do patrimônio exposto',
      'Falhas contratuais relevantes',
      'Custos excessivos por má negociação',
      'Prejuízos por apólices mal estruturadas',
    ],
  },
  {
    icon: 'ri-roadster-line',
    title: 'Frotas e Veículos',
    color: 'green',
    description:
      'Proprietários e empresas com veículos segurados que desejam revisar custos, perfil de uso, franquias, cláusulas e limites de cobertura.',
    risks: [
      'Uso real incompatível com a apólice',
      'Franquias mal posicionadas',
      'Cobertura insuficiente em perda total',
      'Renovação automática sem revisão técnica',
    ],
  },
  {
    icon: 'ri-hospital-line',
    title: 'Saúde e Vida',
    color: 'orange',
    description:
      'Contratos de plano de saúde e seguro de vida que precisam ser revisitados para corrigir capitais, exclusões, reajustes e limitações assistenciais.',
    risks: [
      'Capitais defasados no seguro de vida',
      'Cobertura médica ineficiente',
      'Custos altos sem contrapartida',
      'Limitações contratuais críticas',
    ],
  },
];

const benefits = [
  {
    icon: 'ri-search-eye-line',
    title: 'Diagnóstico Técnico Especializado',
    description:
      'Leitura profissional da apólice vigente para identificar falhas, exclusões, excessos de custo e oportunidades concretas de melhoria.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Economia Real',
    description:
      'A reavaliação pode reduzir gastos desnecessários e reposicionar o contrato para um custo mais coerente com o risco real.',
  },
  {
    icon: 'ri-file-shield-2-line',
    title: 'Correção de Falhas Contratuais',
    description:
      'Revisão criteriosa para eliminar cláusulas perigosas, limitações ocultas e estruturas que fragilizam a indenização.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Proteção Mais Eficiente',
    description:
      'Ajuste de coberturas, capitais e condições para que o contrato proteja de verdade o patrimônio do segurado.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Revisão Mesmo com Seguro Vigente',
    description:
      'Não é preciso esperar vencer: contratos ativos podem e devem ser reavaliados tecnicamente.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Assessoria da IBX Brasil',
    description:
      'Atuação profissional na leitura, correção, negociação e acompanhamento do contrato para decisões mais seguras.',
  },
];

export default function ReavaliacaoFinanceiraSegurosPage() {
  const linesRef = useRef<HTMLElement>(null);
  const processRef = useRef<HTMLElement>(null);
  const casesRef = useRef<HTMLElement>(null);
  const benefitsRef = useRef<HTMLElement>(null);

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Reavaliação Financeira e Técnica de Contratos de Seguros',
    provider: {
      '@type': 'Organization',
      name: 'IBX Brasil',
    },
    description:
      'Assessoria especializada para reavaliar contratos vigentes de seguros, reduzir custos, corrigir falhas contratuais e melhorar a proteção patrimonial.',
    serviceType: 'Consultoria em Reavaliação de Seguros',
    areaServed: 'BR',
  };

  return (
    <>
      <SEOHead
        title="Reavaliação Financeira de Seguros | Reduza Custos e Corrija Falhas"
        description="Reavalie seu contrato de seguro mesmo durante a vigência. Reduza custos, corrija falhas contratuais e melhore a proteção em seguro auto, vida, saúde, incêndio e outros ramos."
        canonical="https://ibxbrasil.com.br/servicos/reavaliacao-financeira-seguros"
        ogImage="https://readdy.ai/api/search-image?query=professional%20insurance%20contract%20review%20business%20and%20personal%20policies%20modern%20office%20documents%20risk%20analysis%20premium%20corporate%20photography&width=1200&height=630&seq=seo-reavaliacao-seguros-001&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />

      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <PageHero
          badge="ASSESSORIA ESPECIALIZADA • REAVALIAÇÃO DE SEGUROS"
          title="Mesmo com o seguro vigente, seu contrato ainda pode custar menos e proteger mais"
          highlightedWord="proteger"
          highlightColor="orange"
          subtitle="A IBX Brasil reavalia contratos ativos de seguros para reduzir custos, corrigir falhas contratuais e fortalecer a proteção patrimonial em diversos ramos."
          ctaText="Solicitar Reavaliação Técnica"
          ctaLink="https://wa.me/5511976565040?text=Ol%C3%A1!%20Quero%20fazer%20uma%20*reavalia%C3%A7%C3%A3o%20financeira%20e%20t%C3%A9cnica*%20do%20meu%20contrato%20de%20seguro.%0A%0AQuero%20analisar%20*custos%2C%20falhas%20contratuais%20e%20melhorias%20de%20prote%C3%A7%C3%A3o*.%0A%0APodemos%20conversar%3F"
          ctaSecondary={{
            text: 'Ver outros serviços',
            link: '/servicos',
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=insurance%20contract%20review%20for%20car%20life%20health%20business%20and%20fire%20insurance%20modern%20professional%20office%20premium%20consulting%20environment&width=1920&height=700&seq=reavaliacao-hero-bg-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Ramos */}
        <section ref={linesRef} className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Em Quais Contratos a Reavaliação Faz Sentido?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A revisão técnica não se limita ao seguro auto. Ela fortalece diversos ramos com impacto direto em custo, cobertura e segurança contratual.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {insuranceLines.map((line, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-300 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${line.icon} text-2xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{line.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{line.description}</p>
                  <ul className="space-y-2">
                    {line.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-orange-600"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Processo */}
        <section ref={processRef} className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Como Funciona a Reavaliação em 4 Etapas
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Método estruturado para descobrir desperdícios, corrigir fragilidades e fortalecer a proteção do contrato vigente.
              </p>
            </div>

            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="flex flex-col lg:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center">
                        <span className="text-white font-bold text-2xl">{step.number}</span>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 sm:mb-0">{step.title}</h3>
                        <span className="inline-flex items-center gap-2 text-sm text-orange-600 font-semibold">
                          <i className="ri-time-line"></i>
                          {step.duration}
                        </span>
                      </div>

                      <p className="text-gray-700 mb-4 leading-relaxed">{step.description}</p>

                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <i className="ri-file-list-3-line text-orange-600"></i>
                          Entregas desta etapa:
                        </h4>
                        <ul className="space-y-1">
                          {step.deliverables.map((deliverable, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                              <i className="ri-checkbox-circle-fill text-orange-600 text-xs"></i>
                              {deliverable}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Casos */}
        <section ref={casesRef} className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Para Quem Essa Reavaliação é Indicada?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Segurados em todo o Brasil que já possuem contratos ativos e querem mais economia, mais clareza contratual e mais proteção.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${
                    useCase.color === 'orange'
                      ? 'from-orange-50 to-orange-100/50 border-orange-200'
                      : useCase.color === 'petrol'
                      ? 'from-petrol-50 to-petrol-100/50 border-petrol-200'
                      : 'from-green-50 to-green-100/50 border-green-200'
                  } rounded-2xl p-8 border-2 hover:shadow-xl transition-all`}
                >
                  <div
                    className={`w-14 h-14 ${
                      useCase.color === 'orange'
                        ? 'bg-orange-600'
                        : useCase.color === 'petrol'
                        ? 'bg-petrol-600'
                        : 'bg-green-600'
                    } rounded-xl flex items-center justify-center mb-6`}
                  >
                    <i className={`${useCase.icon} text-2xl text-white`}></i>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">{useCase.description}</p>

                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <i className="ri-alert-line text-orange-600"></i>
                      Situações típicas de risco:
                    </h4>
                    <ul className="space-y-2">
                      {useCase.risks.map((risk, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-arrow-right-s-line text-orange-600"></i>
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefícios */}
        <section ref={benefitsRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                O Que a IBX Brasil Entrega na Reavaliação
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Benefícios concretos para quem deseja revisar contratos vigentes com inteligência técnica.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${benefit.icon} text-xl text-orange-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Seu Seguro Vigente Pode Custar Menos e Proteger Mais
            </h2>
            <p className="text-xl text-orange-100 mb-10 max-w-2xl mx-auto">
              Solicite uma reavaliação técnica com a IBX Brasil e descubra falhas contratuais, oportunidades de economia e melhorias reais na sua proteção.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/5511976565040?text=Ol%C3%A1!%20Quero%20fazer%20uma%20*reavalia%C3%A7%C3%A3o%20financeira%20e%20t%C3%A9cnica*%20do%20meu%20contrato%20de%20seguro.%0A%0AQuero%20analisar%20*custos%2C%20falhas%20contratuais%20e%20melhorias%20de%20prote%C3%A7%C3%A3o*.%0A%0APodemos%20conversar%3F"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full sm:w-auto bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all hover:scale-105 hover:shadow-xl whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                Solicitar Reavaliação Técnica
              </a>

              <a
                href="/servicos"
                className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Ver Outros Serviços
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}